// src/components/admin/ApiKeyManagement.js
import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Tooltip,
  Chip,
  CircularProgress,
  Alert,
  Snackbar,
} from "@mui/material";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  ContentCopy as CopyIcon,
} from "@mui/icons-material";
import { format } from "date-fns";

const ApiKeyManagement = () => {
  const [apiKeys, setApiKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // For creating new API key
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [keyDescription, setKeyDescription] = useState("");
  const [creatingKey, setCreatingKey] = useState(false);

  // For displaying generated API key
  const [newKeyDialogOpen, setNewKeyDialogOpen] = useState(false);
  const [generatedApiKey, setGeneratedApiKey] = useState("");

  // For deleting API key
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [keyToDelete, setKeyToDelete] = useState(null);

  // For copy notification
  const [copySnackbarOpen, setCopySnackbarOpen] = useState(false);

  useEffect(() => {
    fetchApiKeys();
  }, []);

  const fetchApiKeys = async () => {
    try {
      setLoading(true);
      const res = await axios.get("/api/keys");
      setApiKeys(res.data.apiKeys);
      setError("");
    } catch (error) {
      setError("Failed to load API keys. Please try again.");
      console.error("Error fetching API keys:", error);
    } finally {
      setLoading(false);
    }
  };

  const openCreateDialog = () => {
    setKeyDescription("");
    setCreateDialogOpen(true);
  };

  const closeCreateDialog = () => {
    setCreateDialogOpen(false);
  };

  const handleCreateApiKey = async () => {
    if (!keyDescription.trim()) {
      return;
    }

    try {
      setCreatingKey(true);
      const res = await axios.post("/api/keys", {
        description: keyDescription,
      });

      // Close the create dialog
      closeCreateDialog();

      // Show the newly generated API key
      setGeneratedApiKey(res.data.apiKey);
      setNewKeyDialogOpen(true);

      // Refresh the list of API keys
      fetchApiKeys();
    } catch (error) {
      setError("Failed to create API key. Please try again.");
      console.error("Error creating API key:", error);
    } finally {
      setCreatingKey(false);
    }
  };

  const closeNewKeyDialog = () => {
    setNewKeyDialogOpen(false);
    setGeneratedApiKey("");
  };

  const copyApiKey = () => {
    navigator.clipboard.writeText(generatedApiKey);
    setCopySnackbarOpen(true);
  };

  const handleCopySnackbarClose = () => {
    setCopySnackbarOpen(false);
  };

  const openDeleteDialog = (key) => {
    setKeyToDelete(key);
    setDeleteDialogOpen(true);
  };

  const closeDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setKeyToDelete(null);
  };

  const handleDeleteApiKey = async () => {
    if (!keyToDelete) return;

    try {
      await axios.delete(`/api/keys/${keyToDelete._id}`);
      // Refresh the list of API keys
      const updatedKeys = apiKeys.filter((key) => key._id !== keyToDelete._id);
      setApiKeys(updatedKeys);
      closeDeleteDialog();
    } catch (error) {
      setError("Failed to delete API key. Please try again.");
      console.error("Error deleting API key:", error);
    }
  };

  const formatDate = (dateString) => {
    try {
      return format(new Date(dateString), "MMM dd, yyyy");
    } catch (error) {
      return "Invalid Date";
    }
  };

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 3 }}>
        <Typography variant="h6">
          API Keys for Third-Party Integrations
        </Typography>

        <Box>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchApiKeys}
            sx={{ mr: 2 }}
          >
            Refresh
          </Button>

          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={openCreateDialog}
          >
            Generate New API Key
          </Button>
        </Box>
      </Box>

      <Typography variant="body2" sx={{ mb: 3 }}>
        These API keys allow third-party systems to access equipment data
        through our integration API. Keep your API keys secure and never share
        them in public repositories or client-side code.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
          <CircularProgress />
        </Box>
      ) : apiKeys.length === 0 ? (
        <Alert severity="info">
          No API keys found. Generate your first API key to enable third-party
          integrations.
        </Alert>
      ) : (
        <TableContainer component={Paper}>
          <Table aria-label="API keys table">
            <TableHead>
              <TableRow>
                <TableCell>Description</TableCell>
                <TableCell>Created</TableCell>
                <TableCell>Last Used</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {apiKeys.map((key) => (
                <TableRow key={key._id} hover>
                  <TableCell>{key.description}</TableCell>
                  <TableCell>{formatDate(key.createdAt)}</TableCell>
                  <TableCell>
                    {key.lastUsed ? formatDate(key.lastUsed) : "Never"}
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="Delete API Key">
                      <IconButton
                        aria-label="delete"
                        color="error"
                        onClick={() => openDeleteDialog(key)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Create API Key Dialog */}
      <Dialog open={createDialogOpen} onClose={closeCreateDialog}>
        <DialogTitle>Generate New API Key</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Please provide a description for this API key to help you identify
            its purpose later.
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            label="Description"
            fullWidth
            variant="outlined"
            value={keyDescription}
            onChange={(e) => setKeyDescription(e.target.value)}
            placeholder="e.g., CRM Integration, Mobile App, Reporting System"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeCreateDialog}>Cancel</Button>
          <Button
            onClick={handleCreateApiKey}
            variant="contained"
            disabled={!keyDescription.trim() || creatingKey}
          >
            {creatingKey ? "Generating..." : "Generate"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Display New API Key Dialog */}
      <Dialog
        open={newKeyDialogOpen}
        onClose={closeNewKeyDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>New API Key Generated</DialogTitle>
        <DialogContent>
          <DialogContentText
            sx={{ mb: 2, fontWeight: "bold", color: "error.main" }}
          >
            Important: This is the only time your API key will be displayed.
            Please copy it now!
          </DialogContentText>

          <Paper
            sx={{
              p: 2,
              backgroundColor: "grey.100",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Typography
              variant="body2"
              sx={{
                fontFamily: "monospace",
                overflowX: "auto",
                whiteSpace: "nowrap",
                flex: 1,
                mr: 2,
              }}
            >
              {generatedApiKey}
            </Typography>
            <IconButton onClick={copyApiKey} color="primary">
              <CopyIcon />
            </IconButton>
          </Paper>

          <Typography variant="body2">
            To use this API key, include it in your API requests with the
            'X-API-Key' header.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeNewKeyDialog} variant="contained">
            I've Copied My API Key
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete API Key Dialog */}
      <Dialog open={deleteDialogOpen} onClose={closeDeleteDialog}>
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the API key "
            {keyToDelete?.description}"? This action cannot be undone, and any
            services using this key will lose access.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteDialog} color="primary">
            Cancel
          </Button>
          <Button onClick={handleDeleteApiKey} color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Copy Success Snackbar */}
      <Snackbar
        open={copySnackbarOpen}
        autoHideDuration={3000}
        onClose={handleCopySnackbarClose}
        message="API key copied to clipboard"
      />
    </Box>
  );
};

export default ApiKeyManagement;
